// Command palette (DESIGN.md "Shell"): ⌘K / Ctrl+K anywhere in the workspace.
// Start a session, ask Nexus, jump to any page the user can open (IA §4),
// reopen a recent record, find contacts by name or number and deals by name,
// call a typed number, switch theme and set availability. Combobox
// semantics: focus stays in the input, arrows move the active option, Enter
// runs it, Escape closes and focus returns to where it was.
//
// Motion: one highlight element sits under the active option. It glides on
// transform when the pointer moves it and jumps when the keyboard does
// (DESIGN.md: the active option moves instantly while arrowing). Results
// cross-fade when the set changes; the panel enters and leaves on the
// dialog curves (signal.css .sg-palette).

import React, {
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useUserRole } from "../../contexts/UserRoleContext";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { SKINS } from "../../contexts/skins";
import IntegratedDialerContext from "../../contexts/IntegratedDialerContext";
import contactApi from "../../services/contactApi";
import dealApi from "../../services/dealApi";
import { canOpenCrm, getPaletteRoutes } from "./navigation";
import { pushRecent, readRecent } from "./recentStore";
import { openAskNexus } from "../assistant/askNexusStore";
import { AVAILABILITY_OPTIONS, useAvailability } from "./AvailabilityPill";
import useMediaQuery from "../../hooks/useMediaQuery";
import { EASE, MS, usePrefersReducedMotion, usePresence } from "../ui/motion";
import {
  ArrowRightIcon,
  BuildingIcon,
  CallsIcon,
  CampaignIcon,
  ContactsIcon,
  DealIcon,
  HistoryIcon,
  MoonIcon,
  PhoneIcon,
  SearchIcon,
  SparkIcon,
  SunIcon,
} from "./icons";

const RECENT_ICONS = {
  Contact: ContactsIcon,
  Deal: DealIcon,
  Company: BuildingIcon,
  Campaign: CampaignIcon,
  Call: CallsIcon,
};

const digitsOf = (value) => String(value || "").replace(/\D/g, "");

/** A query that reads as a phone number: digits and phone punctuation only. */
const asPhoneNumber = (query) => {
  const trimmed = query.trim();
  if (!/^\+?[\d\s().-]+$/.test(trimmed)) return null;
  return digitsOf(trimmed).length >= 3 ? trimmed : null;
};

/**
 * Word-start matching: "rep" finds Reports, "agent perf" finds Agent
 * performance, "cr" finds CRM integration by initials. Mid-word fragments do
 * not match, so typing a contact's name is not drowned in page noise.
 * 0 means no match.
 */
const score = (text, query) => {
  if (!query) return 1;
  const hay = text.toLowerCase().replace(/[:·&/]+/g, " ").replace(/\s+/g, " ");
  const needle = query.toLowerCase().trim().replace(/\s+/g, " ");
  if (hay.startsWith(needle)) return 100 - hay.length;
  if (hay.includes(` ${needle}`)) return 60 - hay.length;
  const initials = hay
    .split(" ")
    .map((word) => word[0] || "")
    .join("");
  if (needle.length > 1 && initials.startsWith(needle)) return 40 - hay.length;
  return 0;
};

const contactName = (contact) =>
  [contact.firstName ?? contact.first_name, contact.lastName ?? contact.last_name]
    .filter(Boolean)
    .join(" ") ||
  contact.name ||
  contact.email ||
  contact.phone ||
  "Unnamed contact";

const dealTitle = (deal) => deal.title || deal.name || "Untitled deal";

const dealMeta = (deal) =>
  [deal.customerName || deal.company?.name, deal.pipeline_stage?.name || deal.stageName]
    .filter(Boolean)
    .join(" · ");

const CommandPalette = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const roleContext = useUserRole();
  const role = roleContext?.role;
  // Demo workspaces have calling disabled server-side; do not offer it.
  const canCall =
    !user?.demo &&
    (roleContext?.hasPermission ? roleContext.hasPermission("canCall") : true);
  const { darkMode, toggleDarkMode, skin, setSkin, skinForcesDark } = useTheme();
  const dialer = useContext(IntegratedDialerContext);
  const availability = useAvailability();

  const [query, setQuery] = useState("");
  const narrow = useMediaQuery("(max-width: 479px)");
  const [activeIndex, setActiveIndex] = useState(0);
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [contactState, setContactState] = useState("idle");
  const [recent, setRecent] = useState([]);
  // The highlight glides only when the pointer moved it.
  const [glide, setGlide] = useState(false);
  const [highlight, setHighlight] = useState(null);
  const reduced = usePrefersReducedMotion();
  const showDeals = canOpenCrm(user?.role || role);
  const userIdRef = useRef(user?.id);
  userIdRef.current = user?.id;
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const returnFocusRef = useRef(null);
  const listId = useId();
  // Stays mounted for its 140 ms exit (scale .98 + fade, scrim fades).
  const presence = usePresence(open, { exitMs: MS.exitSlow });

  // Global shortcut. Ignored while typing in another editable field only when
  // no modifier is held, so ⌘K always works.
  useEffect(() => {
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  // Remember and restore focus around the dialog's lifetime.
  useEffect(() => {
    if (!open) return undefined;
    returnFocusRef.current = document.activeElement;
    setQuery("");
    setActiveIndex(0);
    setRecent(readRecent(userIdRef.current));
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => {
      cancelAnimationFrame(frame);
      const target = returnFocusRef.current;
      if (target && typeof target.focus === "function") target.focus();
    };
  }, [open]);

  // Contact search through the existing contacts API, debounced.
  useEffect(() => {
    if (!open) return undefined;
    const term = query.trim();
    if (term.length < 2) {
      setContacts([]);
      setDeals([]);
      setContactState("idle");
      return undefined;
    }
    let cancelled = false;
    setContactState("loading");
    const timer = setTimeout(() => {
      const contactSearch = contactApi
        .getAll({ search: term, page: 1, limit: 6 })
        .then((data) => (data?.contacts || []).slice(0, 6));
      // Deals by name; a number is a contact's, never a deal's. A failed
      // deal search drops the group and leaves contacts standing.
      const dealSearch =
        showDeals && !asPhoneNumber(term)
          ? Promise.resolve()
              .then(() => dealApi.getAll({ search: term, page: 1, limit: 4 }))
              .then((data) => (data?.deals || []).slice(0, 4))
              .catch(() => [])
          : Promise.resolve([]);
      Promise.all([contactSearch, dealSearch])
        .then(([foundContacts, foundDeals]) => {
          if (cancelled) return;
          setContacts(foundContacts);
          setDeals(foundDeals);
          setContactState("done");
        })
        .catch(() => {
          if (cancelled) return;
          setContacts([]);
          setDeals([]);
          setContactState("error");
        });
    }, 180);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, open, showDeals]);

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  const placeCall = useCallback(
    async (number, contact) => {
      close();
      if (!dialer?.startCall) return;
      await dialer.startCall(contact?.phone || number, {
        contactId: contact?.id,
        name: contact ? contactName(contact) : undefined,
      });
    },
    [close, dialer],
  );

  const groups = useMemo(() => {
    const term = query.trim();
    const out = [];

    const phone = asPhoneNumber(term);
    if (phone && canCall && dialer?.startCall) {
      const match = contacts.find(
        (c) => c.phone && digitsOf(c.phone).endsWith(digitsOf(phone).slice(-10)),
      );
      out.push({
        label: "Call",
        items: [
          {
            id: "call-number",
            icon: PhoneIcon,
            label: `Call ${phone}`,
            meta: match ? contactName(match) : "Dial",
            run: () => placeCall(phone, match),
          },
        ],
      });
    }

    const pages = getPaletteRoutes(role, user)
      .map((page) => ({
        page,
        rank: Math.max(
          score(page.label, term),
          score(`${page.group} ${page.label}`, term) - 20,
          ...(page.keywords || []).map((word) => score(word, term) - 30),
        ),
      }))
      .filter(({ rank }) => rank > 0)
      .sort((a, b) => (term ? b.rank - a.rank : 0))
      .slice(0, term ? 8 : 12)
      .map(({ page }) => ({
        id: `page-${page.path}-${page.label}`,
        icon: ArrowRightIcon,
        label: page.label,
        meta: page.group,
        run: () => {
          close();
          navigate(page.path);
        },
      }));

    const recentItems = recent
      .filter((entry) => score(entry.label, term) > 0)
      .slice(0, term ? 3 : 5)
      .map((entry) => ({
        id: `recent-${entry.path}`,
        icon: RECENT_ICONS[entry.kind] || HistoryIcon,
        label: entry.label,
        meta: entry.kind,
        run: () => {
          close();
          navigate(entry.path);
        },
      }));
    // An empty query offers what was open last before the full page list.
    if (!term && recentItems.length) out.push({ label: "Recent", items: recentItems });
    if (pages.length) out.push({ label: "Pages", items: pages });

    if (contacts.length) {
      out.push({
        label: "Contacts",
        items: contacts.map((contact) => ({
          id: `contact-${contact.id}`,
          icon: ContactsIcon,
          label: contactName(contact),
          meta: contact.phone || contact.company?.name || contact.email || "",
          mono: Boolean(contact.phone),
          hint: canCall && contact.phone && dialer?.startCall ? "⇧↵ call" : null,
          run: (event) => {
            if (event?.shiftKey && canCall && contact.phone && dialer?.startCall) {
              placeCall(contact.phone, contact);
              return;
            }
            close();
            pushRecent(user?.id, {
              path: `/contacts/${contact.id}`,
              label: contactName(contact),
              kind: "Contact",
            });
            navigate(`/contacts/${contact.id}`);
          },
        })),
      });
    }

    if (deals.length) {
      out.push({
        label: "Deals",
        items: deals.map((deal) => ({
          id: `deal-${deal.id}`,
          icon: DealIcon,
          label: dealTitle(deal),
          meta: dealMeta(deal),
          run: () => {
            close();
            pushRecent(user?.id, { path: `/deals/${deal.id}`, label: dealTitle(deal), kind: "Deal" });
            navigate(`/deals/${deal.id}`);
          },
        })),
      });
    }

    if (term && recentItems.length) out.push({ label: "Recent", items: recentItems });

    // The two things a user most often wants from anywhere: start dialing,
    // or ask the assistant. Suggested first while the query is empty.
    const quickActions = [
      {
        id: "start-session",
        icon: PhoneIcon,
        label: "Start session",
        meta: "Dialer",
        run: () => {
          close();
          navigate("/dialer");
        },
      },
      {
        id: "ask-nexus",
        icon: SparkIcon,
        label: "Ask your coach",
        meta: "Coach",
        keywords: ["ask nexus", "assistant"],
        run: () => {
          close();
          openAskNexus("ask");
        },
      },
    ];
    if (!term) out.unshift({ label: "Suggested", items: quickActions });

    const actions = [
      ...(term ? quickActions : []),
      ...SKINS.filter((option) => option.id !== skin).map((option) => ({
        id: `skin-${option.id}`,
        icon: SparkIcon,
        label: `Switch interface to ${option.name}`,
        meta: "Interface",
        run: () => {
          setSkin(option.id);
          close();
        },
      })),
      ...(skinForcesDark ? [] : [{
        id: "theme",
        icon: darkMode ? SunIcon : MoonIcon,
        label: darkMode ? "Switch to light theme" : "Switch to dark theme",
        meta: "Theme",
        run: () => {
          toggleDarkMode();
          close();
        },
      }]),
      ...(availability
        ? AVAILABILITY_OPTIONS.map((option) => ({
            id: `availability-${option.value}`,
            dot: option.tone || "idle",
            label: `Set availability: ${option.label}`,
            meta: availability.status.value === option.value ? "Current" : "Availability",
            run: () => {
              availability.setStatus(option.value);
              close();
            },
          }))
        : []),
    ].filter(
      (action) =>
        Math.max(
          score(action.label, term),
          ...(action.keywords || []).map((word) => score(word, term)),
        ) > 0,
    );
    if (actions.length) out.push({ label: "Actions", items: actions });

    return out;
  }, [
    query,
    contacts,
    deals,
    recent,
    role,
    user,
    canCall,
    dialer,
    darkMode,
    availability,
    toggleDarkMode,
    skin,
    setSkin,
    skinForcesDark,
    navigate,
    close,
    placeCall,
  ]);

  const flat = useMemo(() => groups.flatMap((group) => group.items), [groups]);

  useEffect(() => {
    setActiveIndex(0);
    setGlide(false);
  }, [query]);

  useEffect(() => {
    if (activeIndex >= flat.length) setActiveIndex(Math.max(flat.length - 1, 0));
  }, [flat.length, activeIndex]);

  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector('[aria-selected="true"]')
      ?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, open]);

  // Results cross-fade when the set changes, not on a keystroke that
  // leaves it the same.
  const signature = flat.map((item) => item.id).join("|");

  // The highlight follows the active option on transform.
  useLayoutEffect(() => {
    if (!presence.mounted) return;
    const option = listRef.current?.querySelector('[role="option"][aria-selected="true"]');
    if (!option) {
      setHighlight(null);
      return;
    }
    const next = { y: option.offsetTop, height: option.offsetHeight };
    setHighlight((current) =>
      current && current.y === next.y && current.height === next.height ? current : next,
    );
  }, [presence.mounted, activeIndex, signature]);
  const resultsRef = useRef(null);
  const lastSignature = useRef(signature);
  useLayoutEffect(() => {
    if (lastSignature.current === signature) return undefined;
    lastSignature.current = signature;
    const element = resultsRef.current;
    if (!element || typeof element.animate !== "function" || reduced) return undefined;
    const animation = element.animate([{ opacity: 0.35 }, { opacity: 1 }], {
      duration: MS.micro,
      easing: `cubic-bezier(${EASE.standard.join(",")})`,
    });
    return () => animation.cancel();
  }, [signature, reduced]);

  if (!presence.mounted) return null;

  const optionId = (index) => `${listId}-opt-${index}`;
  const active = flat[activeIndex];

  const onKeyDown = (event) => {
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) setGlide(false);
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => (flat.length ? (index + 1) % flat.length : 0));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) =>
        flat.length ? (index - 1 + flat.length) % flat.length : 0,
      );
    } else if (event.key === "Home" && event.ctrlKey) {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === "End" && event.ctrlKey) {
      event.preventDefault();
      setActiveIndex(Math.max(flat.length - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      active?.run(event);
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      close();
    } else if (event.key === "Tab") {
      // The input is the only tab stop; keep focus inside the dialog.
      event.preventDefault();
    }
  };

  let running = -1;

  return createPortal(
    <div className="sg-palette-layer" data-state={presence.state}>
      <div className="sg-palette-scrim" aria-hidden="true" onMouseDown={close} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        className="sg-palette sg-pop"
        data-state={presence.state}
        onAnimationEnd={presence.onAnimationEnd}
      >
        <div className="sg-palette-search">
          <SearchIcon size={16} className="text-sg-ink-3" />
          <input
            ref={inputRef}
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={active ? optionId(activeIndex) : undefined}
            aria-label="Search pages, contacts and actions"
            placeholder={narrow ? "Search or dial" : "Search pages, contacts, or type a number to call"}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            spellCheck={false}
            autoComplete="off"
          />
          <kbd className="sg-kbd">Esc</kbd>
        </div>
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label="Results"
          className="sg-palette-list"
        >
          {highlight && (
            <span
              className="sg-palette-highlight"
              aria-hidden="true"
              data-glide={(glide && !reduced) || undefined}
              style={{ height: highlight.height, transform: `translate3d(0, ${highlight.y}px, 0)` }}
            />
          )}
          <div ref={resultsRef} className="sg-palette-results">
          {groups.map((group) => (
            <div key={group.label} role="group" aria-label={group.label}>
              <div className="sg-palette-group sg-micro" aria-hidden="true">
                {group.label}
              </div>
              {group.items.map((item) => {
                running += 1;
                const index = running;
                const selected = index === activeIndex;
                const Icon = item.icon;
                return (
                  <div
                    key={item.id}
                    id={optionId(index)}
                    role="option"
                    aria-selected={selected}
                    className="sg-palette-option"
                    onMouseMove={() => {
                      if (selected) return;
                      setGlide(true);
                      setActiveIndex(index);
                    }}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={(event) => item.run(event)}
                  >
                    <span className="sg-palette-icon">
                      {Icon ? (
                        <Icon size={15} />
                      ) : (
                        <span
                          className="sg-dot"
                          data-tone={item.dot === "idle" ? undefined : item.dot}
                        />
                      )}
                    </span>
                    <span className="sg-palette-label">{item.label}</span>
                    {item.hint && selected && (
                      <span className="sg-palette-hint">{item.hint}</span>
                    )}
                    {item.meta && (
                      <span className={`sg-palette-meta ${item.mono ? "font-mono" : ""}`}>
                        {item.meta}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          </div>
          {flat.length === 0 && (
            <PaletteEmpty
              query={query.trim()}
              searching={contactState === "loading"}
              callingOff={Boolean(asPhoneNumber(query.trim())) && !canCall}
            />
          )}
        </div>
        <div className="sg-palette-foot">
          <span>
            <kbd className="sg-kbd">↑</kbd>
            <kbd className="sg-kbd">↓</kbd> move
          </span>
          <span>
            <kbd className="sg-kbd">↵</kbd> open
          </span>
          {contactState === "loading" && (
            <span className="ml-auto">Searching contacts…</span>
          )}
          {contactState === "error" && (
            <span className="ml-auto">Contact search is unavailable</span>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

/** No results: what was searched, what to try, and why calling is absent. */
const PaletteEmpty = ({ query, searching, callingOff }) => {
  if (searching) {
    return (
      <div className="sg-palette-empty" role="status">
        <span className="sg-btn-spinner" aria-hidden="true" />
        <p className="sg-palette-empty-title">Searching contacts and deals…</p>
      </div>
    );
  }
  return (
    <div className="sg-palette-empty" role="status">
      <span className="sg-palette-empty-icon" aria-hidden="true">
        {callingOff ? <PhoneIcon size={16} /> : <SearchIcon size={16} />}
      </span>
      <p className="sg-palette-empty-title">
        {callingOff ? (
          <>
            Calling is off in this workspace
          </>
        ) : (
          <>
            No matches for <span className="sg-palette-empty-q">“{query}”</span>
          </>
        )}
      </p>
      <p className="sg-palette-empty-hint">
        {callingOff
          ? "Demo workspaces can browse contacts but cannot place calls."
          : "Try a page name, a contact’s name, a deal, or a phone number to call."}
      </p>
    </div>
  );
};

export default CommandPalette;
